import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Slider,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  CropFree,
  Refresh,
  Save,
  PhotoCamera,
} from '@mui/icons-material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageUploadCropProps {
  open: boolean;
  onClose: () => void;
  onImageSave: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
  title?: string;
  aspectRatio?: number; // Ex: 1 para quadrado, 16/9 para widescreen
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 a 1.0
  outputFormat?: 'jpeg' | 'png' | 'webp' | 'auto';
  circularCrop?: boolean;
  minCropSize?: number;
}

const ImageUploadCrop: React.FC<ImageUploadCropProps> = ({
  open,
  onClose,
  onImageSave,
  title = 'Upload e Editar Imagem',
  aspectRatio,
  maxWidth = 800,
  maxHeight = 600,
  quality = 0.9,
  outputFormat = 'auto',
  circularCrop = false,
  minCropSize = 50,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'upload' | 'crop'>('upload');
  const [maintainAspect, setMaintainAspect] = useState(!!aspectRatio);

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Verificar se é uma imagem
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      // Verificar tamanho do arquivo (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('O arquivo deve ter no máximo 10MB.');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Criar preview da imagem
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setImageSrc(result);
        setStep('crop');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Definir crop inicial baseado na proporção desejada
    if (aspectRatio) {
      const cropWidth = Math.min(90, (height * aspectRatio) / width * 90);
      const cropHeight = Math.min(90, cropWidth / aspectRatio);
      
      setCrop({
        unit: '%',
        width: cropWidth,
        height: cropHeight,
        x: (100 - cropWidth) / 2,
        y: (100 - cropHeight) / 2,
      });
    }
  }, [aspectRatio]);

  const getCroppedImg = useCallback(
    async (
      image: HTMLImageElement,
      crop: PixelCrop,
      scale: number = 1,
      rotate: number = 0
    ): Promise<{ blob: Blob; url: string }> => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calcular dimensões do canvas
      const pixelRatio = window.devicePixelRatio;
      canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
      canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

      ctx.scale(pixelRatio, pixelRatio);
      ctx.imageSmoothingQuality = 'high';

      const cropX = crop.x * scaleX;
      const cropY = crop.y * scaleY;

      const centerX = image.naturalWidth / 2;
      const centerY = image.naturalHeight / 2;

      ctx.save();

      // Aplicar transformações
      ctx.translate(-cropX, -cropY);
      ctx.translate(centerX, centerY);
      ctx.rotate((rotate * Math.PI) / 180);
      ctx.scale(scale, scale);
      ctx.translate(-centerX, -centerY);

      // Desenhar a imagem
      ctx.drawImage(
        image,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight,
        0,
        0,
        image.naturalWidth,
        image.naturalHeight
      );

      ctx.restore();

      // Redimensionar se necessário
      let finalCanvas = canvas;
      if (canvas.width > maxWidth || canvas.height > maxHeight) {
        finalCanvas = document.createElement('canvas');
        const finalCtx = finalCanvas.getContext('2d');
        
        if (!finalCtx) {
          throw new Error('No 2d context for final canvas');
        }

        const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
        finalCanvas.width = canvas.width * ratio;
        finalCanvas.height = canvas.height * ratio;

        finalCtx.imageSmoothingQuality = 'high';
        finalCtx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
      }

      return new Promise((resolve, reject) => {
        // Determinar o formato de saída
        let mimeType = 'image/jpeg';
        if (outputFormat === 'auto') {
          if (selectedFile) {
            // Se for PNG ou WebP, mantém o formato original para preservar transparência
            if (selectedFile.type === 'image/png' || selectedFile.type === 'image/webp') {
              mimeType = selectedFile.type;
            } else {
              // Para outros formatos (como GIF ou BMP), converte para PNG para ser mais seguro
              // ou mantém JPEG se já for JPEG
              mimeType = selectedFile.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
            }
          }
        } else {
          mimeType = `image/${outputFormat}`;
        }

        finalCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            const url = URL.createObjectURL(blob);
            resolve({ blob, url });
          },
          mimeType,
          quality
        );
      });
    },
    [maxWidth, maxHeight, quality, outputFormat, selectedFile]
  );

  const handleSave = useCallback(async () => {
    if (!imgRef.current || !completedCrop) {
      setError('Por favor, selecione uma área para cortar.');
      return;
    }

    setLoading(true);
    try {
      const { blob, url } = await getCroppedImg(
        imgRef.current,
        completedCrop,
        scale,
        rotate
      );
      
      onImageSave(blob, url);
      handleClose();
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      setError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [completedCrop, scale, rotate, getCroppedImg, onImageSave]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setImageSrc('');
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5,
    });
    setCompletedCrop(undefined);
    setScale(1);
    setRotate(0);
    setError('');
    setLoading(false);
    setStep('upload');
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    setScale(1);
    setRotate(0);
    setCrop({
      unit: '%',
      width: 90,
      height: 90,
      x: 5,
      y: 5,
    });
  }, []);

  const handleBack = useCallback(() => {
    setStep('upload');
    setImageSrc('');
    setSelectedFile(null);
  }, []);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoCamera />
            {title}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: '500px' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {step === 'upload' && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '400px',
              gap: 3
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CloudUpload sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Selecione uma imagem
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Formatos aceitos: JPG, PNG, GIF, WebP (máximo 10MB)
                </Typography>
              </Box>

              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                size="large"
                sx={{ minWidth: 200 }}
              >
                Escolher Arquivo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {aspectRatio && (
                <Typography variant="caption" color="text.secondary">
                  A imagem será cortada na proporção {aspectRatio === 1 ? '1:1 (quadrada)' : `${aspectRatio}:1`}
                </Typography>
              )}
            </Box>
          )}

          {step === 'crop' && imageSrc && (
            <Box>
              <Grid container spacing={3}>
                {/* Área de Crop */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    p: 2,
                    backgroundColor: '#f9f9f9',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px'
                  }}>
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={aspectRatio}
                      minWidth={minCropSize}
                      minHeight={minCropSize}
                      circularCrop={circularCrop}
                      keepSelection
                    >
                      <img
                        ref={imgRef}
                        alt="Crop"
                        src={imageSrc}
                        style={{
                          transform: `scale(${scale}) rotate(${rotate}deg)`,
                          maxWidth: '100%',
                          maxHeight: '400px',
                        }}
                        onLoad={onImageLoad}
                      />
                    </ReactCrop>
                  </Box>
                </Grid>

                {/* Controles */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="h6">Ajustes</Typography>

                    {/* Escala */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Zoom: {Math.round(scale * 100)}%
                      </Typography>
                      <Slider
                        value={scale}
                        onChange={(_, value) => setScale(value as number)}
                        min={0.5}
                        max={3}
                        step={0.1}
                        marks={[
                          { value: 0.5, label: '50%' },
                          { value: 1, label: '100%' },
                          { value: 2, label: '200%' },
                          { value: 3, label: '300%' },
                        ]}
                      />
                    </Box>

                    {/* Rotação */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Rotação: {rotate}°
                      </Typography>
                      <Slider
                        value={rotate}
                        onChange={(_, value) => setRotate(value as number)}
                        min={-180}
                        max={180}
                        step={15}
                        marks={[
                          { value: -180, label: '-180°' },
                          { value: -90, label: '-90°' },
                          { value: 0, label: '0°' },
                          { value: 90, label: '90°' },
                          { value: 180, label: '180°' },
                        ]}
                      />
                    </Box>

                    {/* Manter proporção */}
                    {!aspectRatio && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={maintainAspect}
                            onChange={(e) => setMaintainAspect(e.target.checked)}
                          />
                        }
                        label="Manter proporção"
                      />
                    )}

                    {/* Informações */}
                    <Box sx={{ 
                      backgroundColor: '#f5f5f5', 
                      p: 2, 
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Informações:
                      </Typography>
                      <Typography variant="caption" display="block">
                        Formato: {outputFormat.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Qualidade: {Math.round(quality * 100)}%
                      </Typography>
                      <Typography variant="caption" display="block">
                        Tamanho máx: {maxWidth}x{maxHeight}px
                      </Typography>
                      {completedCrop && (
                        <Typography variant="caption" display="block">
                          Área selecionada: {Math.round(completedCrop.width)}x{Math.round(completedCrop.height)}px
                        </Typography>
                      )}
                    </Box>

                    {/* Botão Reset */}
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleReset}
                      fullWidth
                    >
                      Resetar Ajustes
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          {step === 'crop' && (
            <Button onClick={handleBack} startIcon={<CloudUpload />}>
              Trocar Imagem
            </Button>
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button onClick={handleClose}>
            Cancelar
          </Button>
          
          {step === 'crop' && (
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<Save />}
              disabled={!completedCrop || loading}
            >
              {loading ? 'Processando...' : 'Salvar Imagem'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>Processando imagem...</Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default ImageUploadCrop; 